import React, { FC, useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import IndicatorCard from '../DashboardCharts/IndicatorCard'
import { EmptyState } from '../DashboardCharts/styles'
import { MiniSeg, MiniBtn } from './styles'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

export interface CurveSeries {
  nome: string
  cor: string
  pontos: (number | null)[]
}

interface Props {
  series: CurveSeries[]
  mode: 'daily' | 'cum'
  /** quando omitido, o seletor Diário/Acumulado não aparece (modo fixo). */
  onModeChange?: (m: 'daily' | 'cum') => void
  title?: string
  /** formata os valores do eixo Y e do tooltip (ex.: R$). */
  yFormat?: (n: number) => string
  emptyLabel?: string
  /** rótulos custom do eixo X (janelas por minuto, OP-3517); padrão: D+n. */
  labels?: string[]
  /** título do eixo X; padrão: dia de ciclo (D+n). */
  xTitle?: string
}

/** Curva de vendas comparativa (D+n) — uma linha por evento. OP-3414/OP-3415. */
const SalesCurveComparison: FC<Props> = ({
  series,
  mode,
  onModeChange,
  title = 'Evolução das vendas por dia de ciclo',
  yFormat,
  emptyLabel = 'Selecione dois eventos para comparar a curva de vendas.',
  labels: customLabels,
  xTitle,
}) => {
  const maxLen = series.reduce((m, s) => Math.max(m, s.pontos.length), 0)
  const labels = useMemo(
    () => customLabels ?? Array.from({ length: maxLen }, (_, i) => `D+${i}`),
    [customLabels, maxLen]
  )

  const data = useMemo(
    () => ({
      labels,
      datasets: series.map((s) => ({
        label: s.nome,
        data: s.pontos,
        borderColor: s.cor,
        backgroundColor: mode === 'cum' ? s.cor + '22' : 'transparent',
        borderWidth: 2.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
        fill: mode === 'cum',
        spanGaps: false,
      })),
    }),
    [labels, series, mode]
  )

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: true, position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: {
          callbacks: {
            title: (items) =>
              customLabels ? String(items[0]?.label ?? '') : `Dia ${items[0]?.label ?? ''}`,
            label: (item) => {
              const v = item.parsed.y
              const val = yFormat ? yFormat(v) : v?.toLocaleString('pt-BR')
              return ` ${item.dataset.label}: ${val}`
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxTicksLimit: 12, color: '#B0B0B0', font: { size: 11 } },
          title: {
            display: true,
            text: xTitle ?? 'Dia de venda desde a abertura (D+n)',
            color: '#555555',
            font: { size: 11 },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(16,24,40,.07)' },
          ticks: {
            color: '#B0B0B0',
            font: { size: 11 },
            callback: (value) =>
              yFormat ? yFormat(Number(value)) : Number(value).toLocaleString('pt-BR'),
          },
        },
      },
    }),
    [yFormat, customLabels, xTitle]
  )

  const action = onModeChange ? (
    <MiniSeg>
      <MiniBtn $active={mode === 'daily'} onClick={() => onModeChange('daily')}>
        Diário
      </MiniBtn>
      <MiniBtn $active={mode === 'cum'} onClick={() => onModeChange('cum')}>
        Acumulado
      </MiniBtn>
    </MiniSeg>
  ) : undefined

  return (
    <IndicatorCard title={title} action={action}>
      {series.length < 2 ? (
        <EmptyState>{emptyLabel}</EmptyState>
      ) : (
        <div style={{ height: 340 }}>
          <Line data={data} options={options} />
        </div>
      )}
    </IndicatorCard>
  )
}

export default SalesCurveComparison
